import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, 
  Search, 
  User,
  MoreHorizontal,
  Mail,
  Phone,
  Star,
  Briefcase,
  Eye,
  Edit,
  Trash2,
  Clock,
  Euro,
  CheckCircle2,
  XCircle,
  Loader2,
  Grid3X3,
  List,
  Filter,
  Award,
  TrendingUp,
  Calendar,
  FileText,
  Send
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const CreativesPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    totalProjects: 0,
    averageRating: 0
  });

  // Invite form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    specialties: [],
    hourly_rate: ''
  });

  useEffect(() => {
    fetchCreatives();
  }, []);

  const fetchCreatives = async () => {
    try {
      const response = await api.get('/creatives');
      const data = response.data || [];
      setCreatives(data);
      
      // Calculate stats
      const active = data.filter(c => c.status === 'active').length;
      const pending = data.filter(c => c.status === 'pending').length;
      const totalProjects = data.reduce((sum, c) => sum + (c.projects_completed || 0), 0);
      const ratings = data.filter(c => c.average_rating > 0);
      const avgRating = ratings.length > 0 
        ? ratings.reduce((sum, c) => sum + c.average_rating, 0) / ratings.length 
        : 0;
      
      setStats({
        total: data.length,
        active,
        pending,
        totalProjects,
        averageRating: avgRating.toFixed(1)
      });
    } catch (error) {
      console.error('Error fetching creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const specialties = [
    'Graphisme', 'Vidéo', 'Web', 'Mobile', 'Motion Design', '3D', 'Photo', 'Illustration', 'UX/UI', 'Rédaction'
  ];

  const filteredCreatives = creatives.filter(c => {
    const fullName = `${c.user?.first_name || ''} ${c.user?.last_name || ''}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) || 
                          (c.user?.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesSpecialty = filterSpecialty === 'all' || (c.specialties || []).includes(filterSpecialty);
    return matchesSearch && matchesStatus && matchesSpecialty;
  });

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      await api.post('/users/', {
        email: inviteForm.email,
        password: 'temp123456', // Temporary password
        first_name: inviteForm.first_name,
        last_name: inviteForm.last_name,
        phone: inviteForm.phone,
        role: 'team_member',
        specialties: inviteForm.specialties,
        hourly_rate: parseFloat(inviteForm.hourly_rate) || 35
      });
      
      toast.success('Créatif invité avec succès ! Un email lui a été envoyé.');
      setShowInviteModal(false);
      setInviteForm({ email: '', first_name: '', last_name: '', phone: '', specialties: [], hourly_rate: '' });
      fetchCreatives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (creativeId, newStatus) => {
    try {
      await api.put(`/creatives/${creativeId}/status`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchCreatives();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleSpecialty = (specialty) => {
    const current = inviteForm.specialties;
    if (current.includes(specialty)) {
      setInviteForm({ ...inviteForm, specialties: current.filter(s => s !== specialty) });
    } else {
      setInviteForm({ ...inviteForm, specialties: [...current, specialty] });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      active: 'bg-green-100 text-green-700 border-green-300',
      off: 'bg-red-100 text-red-700 border-red-300',
    };
    const labels = {
      pending: 'En attente',
      active: 'Actif',
      off: 'Désactivé',
    };
    return (
      <Badge className={cn('font-medium border', styles[status] || styles.pending)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const viewCreativeDetail = (creative) => {
    setSelectedCreative(creative);
    setShowDetailModal(true);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${theme.colors?.accent}20` }}
              >
                <User className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              </div>
              Créatifs
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez votre réseau de {stats.total} créatif(s)
            </p>
          </div>
          
          <Button 
            onClick={() => setShowInviteModal(true)}
            style={{ backgroundColor: theme.colors?.accent }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Inviter un créatif
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats.total}</p>
                  <p className="text-xs text-[#64748b]">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats.active}</p>
                  <p className="text-xs text-[#64748b]">Actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-50">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats.pending}</p>
                  <p className="text-xs text-[#64748b]">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats.totalProjects}</p>
                  <p className="text-xs text-[#64748b]">Projets réalisés</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-50">
                  <Star className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats.averageRating}</p>
                  <p className="text-xs text-[#64748b]">Note moyenne</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="off">Désactivés</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Spécialité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes spécialités</SelectItem>
                  {specialties.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* View Mode Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 rounded-none"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 rounded-none"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Creatives Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : filteredCreatives.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                Aucun créatif trouvé
              </h3>
              <p className="text-[#64748b] mb-4">
                Invitez des créatifs à rejoindre votre réseau
              </p>
              <Button onClick={() => setShowInviteModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
                <Plus className="w-4 h-4 mr-2" />
                Inviter un créatif
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCreatives.map(creative => (
              <Card key={creative.id} className="card overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  {/* Header with gradient */}
                  <div 
                    className="h-20 relative"
                    style={{ background: `linear-gradient(135deg, ${theme.colors?.primary} 0%, ${theme.colors?.accent} 100%)` }}
                  >
                    <div className="absolute -bottom-8 left-4">
                      <Avatar 
                        name={`${creative.user?.first_name || ''} ${creative.user?.last_name || ''}`}
                        size="lg"
                        type="creative"
                        className="border-4 border-white"
                      />
                    </div>
                    <div className="absolute top-3 right-3">
                      {getStatusBadge(creative.status)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pt-12 px-4 pb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-[#1a365d]">
                          {creative.user?.first_name} {creative.user?.last_name}
                        </h3>
                        <p className="text-sm text-[#64748b]">{creative.user?.email}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewCreativeDetail(creative)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Voir le profil
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {creative.status === 'active' ? (
                            <DropdownMenuItem 
                              onClick={() => handleStatusChange(creative.id, 'off')}
                              className="text-red-600"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Désactiver
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleStatusChange(creative.id, 'active')}
                              className="text-green-600"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Activer
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Specialties */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(creative.specialties || []).slice(0, 3).map(spec => (
                        <Badge key={spec} variant="outline" className="text-xs">
                          {spec}
                        </Badge>
                      ))}
                      {(creative.specialties || []).length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{creative.specialties.length - 3}
                        </Badge>
                      )}
                    </div>

                    {/* Stats row */}
                    <div className="flex items-center justify-between text-sm border-t pt-3">
                      <div className="flex items-center gap-1 text-[#64748b]">
                        <Euro className="w-4 h-4" />
                        <span>{creative.user?.hourly_rate || 35}€/h</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#64748b]">
                        <Briefcase className="w-4 h-4" />
                        <span>{creative.projects_completed || 0} projets</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500" />
                        <span className="text-[#1a365d] font-medium">{creative.average_rating?.toFixed(1) || '—'}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* List View */
          <Card className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-[#64748b]">Créatif</th>
                  <th className="text-left p-4 font-medium text-[#64748b]">Spécialités</th>
                  <th className="text-left p-4 font-medium text-[#64748b]">Tarif</th>
                  <th className="text-left p-4 font-medium text-[#64748b]">Projets</th>
                  <th className="text-left p-4 font-medium text-[#64748b]">Note</th>
                  <th className="text-left p-4 font-medium text-[#64748b]">Statut</th>
                  <th className="text-right p-4 font-medium text-[#64748b]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCreatives.map(creative => (
                  <tr key={creative.id} className="border-b hover:bg-[#f8fafc]">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Avatar 
                          name={`${creative.user?.first_name || ''} ${creative.user?.last_name || ''}`}
                          size="sm"
                          type="creative"
                        />
                        <div>
                          <p className="font-medium text-[#1a365d]">
                            {creative.user?.first_name} {creative.user?.last_name}
                          </p>
                          <p className="text-xs text-[#64748b]">{creative.user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {(creative.specialties || []).slice(0, 2).map(spec => (
                          <Badge key={spec} variant="outline" className="text-xs">{spec}</Badge>
                        ))}
                        {(creative.specialties || []).length > 2 && (
                          <Badge variant="outline" className="text-xs">+{creative.specialties.length - 2}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4 font-mono">{creative.user?.hourly_rate || 35}€/h</td>
                    <td className="p-4">{creative.projects_completed || 0}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500" />
                        {creative.average_rating?.toFixed(1) || '—'}
                      </div>
                    </td>
                    <td className="p-4">{getStatusBadge(creative.status)}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => viewCreativeDetail(creative)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Invite Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Inviter un créatif</DialogTitle>
              <DialogDescription>
                Le créatif recevra un email d'invitation pour rejoindre votre réseau.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={inviteForm.first_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                    placeholder="Jean"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={inviteForm.last_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                    placeholder="Dupont"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="jean.dupont@email.com"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={inviteForm.phone}
                    onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Tarif horaire (€)</Label>
                  <Input
                    type="number"
                    value={inviteForm.hourly_rate}
                    onChange={(e) => setInviteForm({ ...inviteForm, hourly_rate: e.target.value })}
                    placeholder="35"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Spécialités</Label>
                <div className="flex flex-wrap gap-2">
                  {specialties.map(spec => (
                    <Badge
                      key={spec}
                      variant={inviteForm.specialties.includes(spec) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleSpecialty(spec)}
                      style={inviteForm.specialties.includes(spec) ? { backgroundColor: theme.colors?.accent } : {}}
                    >
                      {spec}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleInvite}
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer l'invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Profil du créatif</DialogTitle>
            </DialogHeader>
            
            {selectedCreative && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                  <Avatar 
                    name={`${selectedCreative.user?.first_name || ''} ${selectedCreative.user?.last_name || ''}`}
                    size="xl"
                    type="creative"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-[#1a365d]">
                        {selectedCreative.user?.first_name} {selectedCreative.user?.last_name}
                      </h3>
                      {getStatusBadge(selectedCreative.status)}
                    </div>
                    <p className="text-[#64748b]">{selectedCreative.user?.email}</p>
                    {selectedCreative.user?.phone && (
                      <p className="text-[#64748b]">{selectedCreative.user?.phone}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold" style={{ color: theme.colors?.accent }}>
                      {selectedCreative.user?.hourly_rate || 35}€
                    </p>
                    <p className="text-xs text-[#64748b]">par heure</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-3 rounded-xl bg-[#f8fafc] text-center">
                    <p className="text-2xl font-bold text-[#1a365d]">{selectedCreative.projects_completed || 0}</p>
                    <p className="text-xs text-[#64748b]">Projets</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#f8fafc] text-center">
                    <p className="text-2xl font-bold text-[#1a365d] flex items-center justify-center">
                      <Star className="w-5 h-5 text-amber-500 mr-1" />
                      {selectedCreative.average_rating?.toFixed(1) || '—'}
                    </p>
                    <p className="text-xs text-[#64748b]">Note</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#f8fafc] text-center">
                    <p className="text-2xl font-bold text-[#1a365d]">{selectedCreative.years_experience || 0}</p>
                    <p className="text-xs text-[#64748b]">Années exp.</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#f8fafc] text-center">
                    <p className="text-2xl font-bold text-[#1a365d]">
                      {selectedCreative.is_partner ? '✓' : '—'}
                    </p>
                    <p className="text-xs text-[#64748b]">Partenaire</p>
                  </div>
                </div>

                {/* Specialties */}
                <div>
                  <h4 className="font-medium text-[#1a365d] mb-2">Spécialités</h4>
                  <div className="flex flex-wrap gap-2">
                    {(selectedCreative.specialties || []).map(spec => (
                      <Badge key={spec} style={{ backgroundColor: `${theme.colors?.accent}20`, color: theme.colors?.accent }}>
                        {spec}
                      </Badge>
                    ))}
                    {(selectedCreative.specialties || []).length === 0 && (
                      <span className="text-[#64748b] text-sm">Aucune spécialité renseignée</span>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {selectedCreative.bio && (
                  <div>
                    <h4 className="font-medium text-[#1a365d] mb-2">Bio</h4>
                    <p className="text-[#64748b]">{selectedCreative.bio}</p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fermer
              </Button>
              <Button style={{ backgroundColor: theme.colors?.accent }}>
                <Edit className="w-4 h-4 mr-2" />
                Modifier le profil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CreativesPage;
